/**
 * Calendar-Specific Utilities
 * Helper functions for calendar operations, email parsing, and event normalization
 */

import {
  normalizeGoogleEvent,
  normalizeMicrosoftEvent,
} from "../../../lib/calendar-utils";
import type {
  CalendarEvent,
  GoogleCalendarEvent,
  MicrosoftCalendarEvent,
  AccountStatus,
} from "../types";
import { logDebug, logWarning, countEventsBySource } from "../utils";

// ============================================================================
// EMAIL PARSING UTILITIES
// ============================================================================

/**
 * Parse selected calendar emails for Google Calendar
 * Handles mapping of hub emails to provider emails
 */
export function parseGoogleCalendarEmails(
  selectedCalendars: string | undefined,
  accountStatus: AccountStatus | undefined
): string[] {
  if (!selectedCalendars || !accountStatus) {
    logDebug("Google Calendar Email Parsing", {
      hasSelectedCalendars: !!selectedCalendars,
      hasAccountStatus: !!accountStatus,
    });
    return [];
  }

  const selectedEmails = selectedCalendars.split(",").map((e) => e.trim());
  const googleEmails: string[] = [];

  // Only proceed if we have a Google account to make API calls
  if (!accountStatus.google.primary && !accountStatus.google.secondary) {
    logDebug("Google Calendar Email Parsing", {
      message: "No Google account available",
    });
    return [];
  }

  selectedEmails.forEach((email) => {
    // Check if it matches current user's Google provider emails
    if (
      accountStatus.google.primaryEmail === email ||
      accountStatus.google.secondaryEmail === email
    ) {
      googleEmails.push(email);
    }
    // If it's a different email, only try Google API if it doesn't match Microsoft email
    else if (email !== accountStatus.microsoftEmail) {
      // For unknown emails, try Google API - if it's not a Google calendar, we'll get a 422
      googleEmails.push(email);
    }
  });

  return googleEmails;
}

/**
 * Parse selected calendar emails for Microsoft Calendar
 * Handles mapping of hub emails to provider emails
 */
export function parseMicrosoftCalendarEmails(
  selectedCalendars: string | undefined,
  accountStatus: AccountStatus | undefined
): string[] {
  if (!selectedCalendars || !accountStatus) {
    return [];
  }

  const selectedEmails = selectedCalendars.split(",").map((e) => e.trim());
  const microsoftEmails: string[] = [];

  // Only proceed if we have a Microsoft account to make API calls
  if (!accountStatus.microsoft) {
    return [];
  }

  selectedEmails.forEach((email) => {
    // Check if it matches current user's Microsoft provider email
    if (accountStatus.microsoftEmail === email) {
      microsoftEmails.push(email);
    }
    // Don't add Gmail addresses to Microsoft fetch list
    // Also don't add if it matches the user's Google emails
    else if (
      !email.includes("@gmail.com") &&
      email !== accountStatus.google.primaryEmail &&
      email !== accountStatus.google.secondaryEmail
    ) {
      // For unknown non-Gmail emails, try Microsoft API
      microsoftEmails.push(email);
    }
  });

  logDebug("Microsoft Calendar Email Parsing", {
    selectedEmails,
    microsoftEmail: accountStatus.microsoftEmail,
    microsoftEmailsToFetch: microsoftEmails,
  });

  return microsoftEmails;
}

// ============================================================================
// EVENT NORMALIZATION UTILITIES
// ============================================================================

/**
 * Normalize Google Calendar events from multiple sources
 */
export function normalizeGoogleCalendarEvents(
  events: GoogleCalendarEvent[],
  calendarEmail?: string
): CalendarEvent[] {
  return events.map((event) => normalizeGoogleEvent(event, calendarEmail));
}

/**
 * Normalize Microsoft Calendar events from multiple sources
 */
export function normalizeMicrosoftCalendarEvents(
  events: MicrosoftCalendarEvent[],
  calendarEmail?: string
): CalendarEvent[] {
  return events.map((event) => normalizeMicrosoftEvent(event, calendarEmail));
}

/**
 * Normalize events from multiple calendars with source tracking
 */
export function normalizeMultipleCalendarEvents(
  results: GoogleCalendarEvent[][],
  calendarEmails: string[],
  provider: "google" | "microsoft"
): CalendarEvent[] {
  const normalizedEvents: CalendarEvent[] = [];

  results.forEach((events, index) => {
    const calendarEmail = calendarEmails[index];
    const normalized =
      provider === "google"
        ? normalizeGoogleCalendarEvents(events as unknown as GoogleCalendarEvent[], calendarEmail)
        : normalizeMicrosoftCalendarEvents(
            events as unknown as MicrosoftCalendarEvent[],
            calendarEmail
          );
    normalizedEvents.push(...normalized);
  });

  logDebug(`${provider} Calendar Events Normalized (Multiple)`, {
    totalRaw: results.reduce((sum, events) => sum + events.length, 0),
    totalNormalized: normalizedEvents.length,
    eventsBySource: countEventsBySource(normalizedEvents),
    selectedEmails: calendarEmails,
  });

  return normalizedEvents;
}

// ============================================================================
// DEBUG LOGGING UTILITIES
// ============================================================================

/**
 * Log calendar query request details
 */
export function logCalendarQueryRequest(
  provider: "google" | "microsoft",
  endpoint: string,
  queryParams: Record<string, string>,
  token: string | null
): void {
  const requestPayload = {
    endpoint,
    queryParams,
    hasToken: !!token,
    tokenPrefix: token ? token.substring(0, 20) + "..." : null,
  };
  console.log(
    `📤 [CLIENT] ${provider.toUpperCase()} Calendar Request:`,
    JSON.stringify(requestPayload, null, 2)
  );
}

/**
 * Log calendar query response details
 */
export function logCalendarQueryResponse(
  provider: "google" | "microsoft",
  response: any
): void {
  console.log(`✅ [CLIENT] ${provider.toUpperCase()} Calendar Response:`, {
    eventsCount: Array.isArray(response) ? response.length : 0,
    hasData: !!response,
    responseType: Array.isArray(response) ? "array" : typeof response,
  });
}

/**
 * Log normalized calendar events
 */
export function logNormalizedCalendarEvents(
  provider: "google" | "microsoft",
  rawCount: number,
  normalizedEvents: CalendarEvent[],
  calendarEmail?: string
): void {
  const eventsBySource = countEventsBySource(normalizedEvents);
  const validEvents = normalizedEvents.filter(
    (e) => e.id && e.title && e.start && e.end
  );
  const invalidEvents = normalizedEvents.length - validEvents.length;

  logDebug(`${provider.toUpperCase()} Calendar Events Normalized`, {
    totalRaw: rawCount,
    totalNormalized: normalizedEvents.length,
    validEvents: validEvents.length,
    invalidEvents,
    eventsBySource,
    calendarEmail,
  });
}

/**
 * Log combined calendar data
 */
export function logCombinedCalendarData(
  totalEvents: number,
  eventsBySource: Record<string, number>,
  googleEvents: number,
  microsoftEvents: number
): void {
  logDebug("Calendar Data Events Fetched by Source", {
    totalEvents,
    eventsBySource,
    googleEvents,
    microsoftEvents,
  });
}
